import { MusicAPIIntegrationID } from '../music-services/ids';
import { DistributorID, distributorNames, ServiceConnectionStatus } from './catalog';

/**
 * Artist portals: the streaming services' own artist dashboards (Spotify for
 * Artists, Deezer for Creators, …) that a Music24 account can connect so we
 * read the artist's real numbers on their behalf.
 *
 * This file is the wire contract between `music24-api` and `music24-app`. Both
 * import from here so the two cannot drift. Keep enums in sync with
 * `music24-api/prisma/main/schema.prisma`.
 *
 * Design: docs/superpowers/specs/2026-09-13-music24-artist-portal-connect-design.md
 */

/**
 * How the artist hands us a session.
 *
 * `oauthRedirect` — the app opens `authUrl` in a browser surface it controls
 * and hands back the redirect url that carries the code (Spotify).
 * `credentials` — the artist types email and password into our form (Deezer).
 * `oauthCallback` — a real OAuth client of ours (YouTube, through Google). The
 * app opens `authUrl` in the system browser (a full-page redirect on the web,
 * `ASWebAuthenticationSession` on iOS, a Custom Tab on Android). The provider
 * sends the artist back to music24-api, which finishes the sign-in and then
 * redirects to the `returnTo` the app sent with `auth/begin`. The outcome is in
 * the query: see `artistPortalConnectReturnParams`. Google refuses embedded web
 * views, so the `oauthRedirect` capture cannot work for it.
 * `capturedSession` — the artist signs in on the service's own page inside a
 * native Music24 app. The app then reads the session values that `auth/begin`
 * names and sends them to `auth/complete` (Ditto Music). A browser cannot read
 * the cookies of another site, so the web app has no such flow. A card of
 * this kind keeps `authKind: null` and says `sessionCaptureAvailable: true`
 * instead, so a client that does not know the flow shows no sign-in button.
 */
export enum ArtistPortalAuthKind {
  oauthRedirect = 'oauthRedirect',
  credentials = 'credentials',
  oauthCallback = 'oauthCallback',
  capturedSession = 'capturedSession',
}

/** The services with a working provider today. Others keep the stub ladder. */
export const artistPortalServices: string[] = [
  MusicAPIIntegrationID.Spotify,
  MusicAPIIntegrationID.Deezer,
  MusicAPIIntegrationID.YoutubeMusic,
  MusicAPIIntegrationID.SoundCloud,
  MusicAPIIntegrationID.Audiomack,
];

/**
 * SoundCloud (2026-10-15) is an `oauthRedirect` like Spotify, but MusicAPI runs the OAuth flow
 * (the hosted flow of the `music24` tenant). The browser goes to `authUrl`, and MusicAPI sends it
 * back to the web app page `/app/catalog/connect/soundCloud?state=<sessionUuid>`. That page calls
 * `auth/complete` with `{ sessionUuid: state, redirectUrl: location.href }`. A native app stops
 * at `redirectUrlPrefix` and sends the same body.
 */
export const artistPortalAuthKindByService: Record<string, ArtistPortalAuthKind> = {
  [MusicAPIIntegrationID.Spotify]: ArtistPortalAuthKind.oauthRedirect,
  [MusicAPIIntegrationID.Deezer]: ArtistPortalAuthKind.credentials,
  [MusicAPIIntegrationID.YoutubeMusic]: ArtistPortalAuthKind.oauthCallback,
  [MusicAPIIntegrationID.SoundCloud]: ArtistPortalAuthKind.oauthRedirect,
  // Audiomack (2026-10-16): the same MusicAPI hosted flow as SoundCloud. The return page is
  // `/app/catalog/connect/audiomack?state=<sessionUuid>`; it ends on the Catalog screen.
  [MusicAPIIntegrationID.Audiomack]: ArtistPortalAuthKind.oauthRedirect,
};

/**
 * Services we have not built yet (early access). The card offers a waiting
 * list: the artist leaves their login email, the row goes to `requested`,
 * support gets a message, and nothing is read from the service until we build
 * its integration.
 *
 * Design: docs/superpowers/specs/2026-10-01-music24-connector-early-access-design.md
 */
export const artistPortalEarlyAccessServices: string[] = [
  MusicAPIIntegrationID.AppleMusic,
  MusicAPIIntegrationID.AmazonMusic,
  MusicAPIIntegrationID.Tidal,
  MusicAPIIntegrationID.Boomplay,
  DistributorID.DistroKid,
  DistributorID.TuneCore,
  DistributorID.CDBaby,
  DistributorID.Ditto,
  DistributorID.UnitedMasters,
  DistributorID.ONErpm,
  DistributorID.TuneCoreJapan,
];

/**
 * The `cause.code` values of a refused Audiomack connect (2026-10-16). `M24_CONNECT_STATE_INVALID`
 * is shared with SoundCloud (`SOUNDCLOUD_ERROR_CODES`).
 */
export const AUDIOMACK_ERROR_CODES = {
  /** The Audiomack login is connected to another Music24 artist. */
  loginInUse: 'M24_AUDIOMACK_LOGIN_IN_USE',
} as const;

/** True when the service offers the early-access waiting list. */
export function isArtistPortalEarlyAccessService(integrationType: string): boolean {
  return artistPortalEarlyAccessServices.includes(integrationType);
}

/**
 * The names of the early-access programmes, for support messages and the
 * backoffice. Brand names, so they are not translated.
 */
export const artistPortalEarlyAccessServiceNames: Record<string, string> = {
  [MusicAPIIntegrationID.AppleMusic]: 'Apple Music for Artists',
  [MusicAPIIntegrationID.AmazonMusic]: 'Amazon Music for Artists',
  [MusicAPIIntegrationID.Tidal]: 'Tidal Artist Home',
  [MusicAPIIntegrationID.Boomplay]: 'Boomplay for Artists',
  [MusicAPIIntegrationID.SoundCloud]: 'SoundCloud',
  [MusicAPIIntegrationID.Audiomack]: 'Audiomack',
  ...distributorNames,
};

/**
 * The query params music24-api adds to `returnTo` when an `oauthCallback`
 * sign-in ends. Example:
 * `…/app/catalog?portalConnect=youtubeMusic&result=pickArtist`.
 *
 * `portalConnect` names the service, so the screen knows which card to open.
 * `reason` is present only on `error` and is a short code, never provider text.
 */
export const artistPortalConnectReturnParams = {
  service: 'portalConnect',
  result: 'result',
  reason: 'reason',
} as const;

/**
 * How an `oauthCallback` sign-in ended.
 *
 * `connected` — one artist (channel) on the login, and it is connected.
 * `pickArtist` — the login holds several; the row is connected with no artist,
 * and the app reads the list from `GET …/artists` and asks which one.
 * `error` — nothing was connected; `reason` says why.
 */
export enum ArtistPortalConnectResult {
  connected = 'connected',
  pickArtist = 'pickArtist',
  error = 'error',
}

/**
 * Why an `oauthCallback` sign-in failed.
 *
 * `denied` — the artist cancelled, or did not grant every scope we asked for.
 * `invalidState` — the sign-in expired (ten minutes) or was used already.
 * `exchangeFailed` — the provider refused the code or did not answer.
 * `noArtists` — the login holds no artist (a Google account with no channel).
 * `notConfigured` — this deploy cannot connect the service right now.
 * `loginInUse` — another Music24 artist holds this login (Audiomack, the return page).
 */
export enum ArtistPortalConnectFailure {
  denied = 'denied',
  invalidState = 'invalidState',
  exchangeFailed = 'exchangeFailed',
  noArtists = 'noArtists',
  notConfigured = 'notConfigured',
  loginInUse = 'loginInUse',
  unknown = 'unknown',
}

/**
 * Keep in sync with `ArtistPortalSyncStatus` in `music24-api/prisma/main/schema.prisma`.
 */
export enum ArtistPortalSyncStatus {
  idle = 'idle',
  queued = 'queued',
  running = 'running',
  ok = 'ok',
  failed = 'failed',
  reauthRequired = 'reauthRequired',
}

/** Keep in sync with `ArtistPortalEntityType` in the Prisma schema. */
export enum ArtistPortalEntityType {
  artist = 'artist',
  track = 'track',
}

/** Keep in sync with `ArtistPortalPlaylistCurator` in the Prisma schema. */
export enum ArtistPortalPlaylistCurator {
  editorial = 'editorial',
  listener = 'listener',
  algorithmic = 'algorithmic',
  other = 'other',
}

export enum ArtistPortalMetric {
  streams = 'streams',
  listeners = 'listeners',
  saves = 'saves',
  playlistAdds = 'playlistAdds',
  followers = 'followers',
  monthlyListeners = 'monthlyListeners',
}

/** Metrics that are a level (last value in range), not a flow (sum over range). */
export const artistPortalLevelMetrics: ArtistPortalMetric[] = [
  ArtistPortalMetric.followers,
  ArtistPortalMetric.monthlyListeners,
];

/** An artist as a portal names it. `externalId` is the id on that service. */
export interface ArtistPortalArtistRef {
  externalId: string;
  name: string;
  imageUrl: string | null;
}

/** One part of a portal sync that a changed page stopped (`ArtistPortalConnectionFields.syncWarnings`). */
export interface ArtistPortalSyncWarning {
  part: string;
  message: string;
}

/**
 * The connection fields added by artist portals to the existing
 * `ServiceConnection` wire shape. Credentials never appear here.
 */
export interface ArtistPortalConnectionFields {
  /** Null for a service with no provider. */
  authKind: ArtistPortalAuthKind | null;
  /** False when the server has no secret key or the service is switched off. */
  connectAvailable: boolean;
  artist: ArtistPortalArtistRef | null;
  externalAccountEmail: string | null;
  syncStatus: ArtistPortalSyncStatus;
  /** ISO datetime. */
  lastSyncAt: string | null;
  /** YYYY-MM-DD — last date with daily rows. */
  syncedThrough: string | null;
  lastSyncError: string | null;
  /**
   * The parts of the last sync (or of the connect) that a changed page of the service stopped
   * (2026-10-20). The other parts stored their data and `syncStatus` stays `ok`. Absent when every
   * part read, and on an old server. `part`: `artists`, `artistDaily`, `catalogue`, `trackDaily`,
   * `playlists`, `earnings`, `dimensional` or `sections`. `message` names the page only.
   */
  syncWarnings?: ArtistPortalSyncWarning[];
  /**
   * True for a service we have not built yet: the card offers the waiting list
   * instead of a sign-in, and a `requested` row means "on the list".
   */
  earlyAccess: boolean;
  /**
   * True when a native app can connect this service with a captured session
   * (`ArtistPortalAuthKind.capturedSession`) on this deploy. Absent on an old
   * server. `authKind` stays null and `connectAvailable` stays false on such a
   * card, so the web app and an old native app keep the waiting list.
   *
   * Legacy: sent ONLY for a service of capture version 1 (Ditto Music). The web
   * app and the released apps treat any card with this field true as a Ditto
   * card, so a newer capture service (DistroKid) never has it and stays on
   * their waiting list. New apps read `sessionCapture`.
   */
  sessionCaptureAvailable?: boolean;
  /**
   * On every card of a captured-session service. Absent on an old server and on
   * every other card.
   */
  sessionCapture?: ArtistPortalSessionCaptureCard;
  /**
   * YouTube only (`youtubeMusic`). One Google sign-in feeds Social (posting) and Streaming
   * (analytics). `analytics`: the sign-in posts but has no Analytics permission. `posting`: the
   * sign-in reads analytics but cannot post (an old Catalog sign-in). Null or absent: nothing is
   * missing. The card shows "Reconnect to add analytics" or "Reconnect to add posting", and the
   * reconnect asks for every permission. See `YouTubeConnectionGap`.
   */
  reconnectToAdd?: YouTubeConnectionGap | null;
  /**
   * SoundCloud only (2026-10-15): how many song uploads wait for their time on this store. A
   * Disconnect cancels them, and the Disconnect dialog says how many. Absent on other cards.
   */
  waitingUploads?: number;
  /**
   * SoundCloud only (2026-10-16): the second sign-in on SoundCloud's own page (a captured web
   * session) that reads the private Insights. Absent on other cards and on an old server.
   * Spec: docs/superpowers/specs/2026-10-16-music24-soundcloud-insights-design.md
   */
  webSession?: ArtistPortalWebSessionCard;
  /**
   * A sign-in-only service (capture version 4, 2026-10-18): the artist signs in now, and Music24
   * builds the reader on the account data later. The app shows the consent line first, sends
   * `POST …/signInConsent`, and only then starts `auth/begin`. Absent on every other card.
   * Spec: docs/music24-connectors/PENDING-SIGNIN.md
   */
  signInConsent?: ArtistPortalSignInConsentCard;
  /**
   * A sign-in-only service: a true line about the stored sign-in, or null. `noArtistAccess`: the
   * sign-in works, but the service gave the login no artist yet (Apple Music for Artists with an
   * access request that waits). The app says so and shows no number. Absent on other cards.
   */
  signInNotice?: ArtistPortalSignInNotice | null;
  /**
   * True on a capture card with a sign-in consent whose service reads numbers (Apple Music for
   * Artists, 2026-10-21): the card shows the artist picker and the numbers like any reader. Absent
   * on a sign-in-only service and on an older server: the card reads no number.
   */
  readsNumbers?: true;
}

/** See `ArtistPortalConnectionFields.signInNotice`. */
export type ArtistPortalSignInNotice = 'noArtistAccess';

/** The consent step of a sign-in-only service. */
export interface ArtistPortalSignInConsentCard {
  /** The version of the consent text that the app shows. The app sends it back with the consent. */
  textVersion: string;
}

/**
 * The version of the consent text (`ARTIST_PORTAL_SIGNIN_CONSENT_TEXT`). A new text gets a new
 * version, and the server then asks for the consent again.
 */
// Version 2 (owner, 2026-10-18): the time span is "over the next few days" (version 1: "weeks").
export const ARTIST_PORTAL_SIGNIN_CONSENT_TEXT_VERSION = 'portal-signin-2026-10-18-2';

/**
 * The consent text of version `ARTIST_PORTAL_SIGNIN_CONSENT_TEXT_VERSION`, in English (owner
 * decision 2026-10-18). The apps show a translation of it (`app.sml.native.portalSignin.consent`).
 * The server stores the version, never a copy of the text.
 */
export const ARTIST_PORTAL_SIGNIN_CONSENT_TEXT =
  'This connection is new. Your numbers come in over the next few days. Our team checks your account data to make sure it reads correctly.';

/** POST /accounts/:accountId/serviceConnections/:integrationType/signInConsent */
export interface ArtistPortalSignInConsentBody {
  textVersion: string;
}

/** The answer of `POST …/signInConsent`. */
export interface ArtistPortalSignInConsentResponse {
  /** ISO datetime. */
  consentedAt: string;
  textVersion: string;
}

/**
 * The `cause.code` of an `auth/begin` of a sign-in-only service with no consent of the caller for
 * the current text version (HTTP 403). The app shows the consent step again.
 */
export const ARTIST_PORTAL_SIGNIN_CONSENT_REQUIRED = 'M24_PORTAL_SIGNIN_CONSENT_REQUIRED';

/** What the second sign-in reads (the chips of the card). */
export type ArtistPortalWebSessionGive =
  | 'dailyPlays'
  | 'listeners'
  | 'countries'
  | 'cities'
  | 'playSources'
  | 'trackPlays';

/** `none`: no second sign-in. `connected`: it reads. `expired`: the service refused it. */
export type ArtistPortalWebSessionState = 'none' | 'connected' | 'expired';

/**
 * The second sign-in of a connection: a session that a native app captures on the service's own
 * page (SoundCloud Insights). The official sign-in of the card is not changed by it.
 */
export interface ArtistPortalWebSessionCard {
  /** A native app of capture version `version` or higher can sign in on this deploy. */
  available: boolean;
  /** The capture protocol the app must know (3: `readyWhen` and `optional`). */
  version: number;
  state: ArtistPortalWebSessionState;
  /** ISO datetime of the last capture, or null. */
  connectedAt: string | null;
  /** ISO datetime of the last signed-in answer, or null. */
  lastSeenAt: string | null;
  gives: ArtistPortalWebSessionGive[];
}

/**
 * What one YouTube sign-in lacks: `analytics` (no `yt-analytics.readonly`) or `posting` (no
 * `youtube.upload`). The Catalog card (`ArtistPortalConnectionFields.reconnectToAdd`) and the
 * Channels card (`reconnectToAdd` of the YouTube channel) show the same value.
 */
export type YouTubeConnectionGap = 'analytics' | 'posting';

/**
 * POST /accounts/:accountId/serviceConnections/:integrationType/auth/begin
 *
 * The body is optional. Only an `oauthCallback` service reads it.
 */
export interface ArtistPortalAuthBeginBody {
  /**
   * Where music24-api sends the artist after an `oauthCallback` sign-in: a page
   * on the web app's origin, or `<app scheme>://oauth/return` for a native app.
   * The server refuses any other address. Default: the web Catalog screen.
   */
  returnTo?: string;
}

/** POST /accounts/:accountId/serviceConnections/:integrationType/auth/begin */
export interface ArtistPortalAuthBeginResponse {
  authUrl: string;
  sessionUuid: string;
  /**
   * `oauthRedirect`: the app must stop loading any url that starts with this
   * and hand it back. `oauthCallback`: the music24-api callback address, for
   * information only. The app does not capture it.
   * `capturedSession`: the app waits for a url that starts with this, then
   * reads the values that `capture` names.
   */
  redirectUrlPrefix: string;
  /** `capturedSession` only: what the native app reads from its web view. */
  capture?: ArtistPortalSessionCapture;
}

/** One cookie the native app reads by its exact name. The app sends its value as a string. */
export interface ArtistPortalCapturedCookieByName {
  /** The cookie name on the service, for example `DittoMusic[jwtprod]`. */
  name: string;
  /** The key of this value in `capturedSession` of `auth/complete`. */
  as: string;
  /**
   * Capture version 3: the app sends the cookie when it is there and leaves the key out when it
   * is not. Absent means required.
   */
  optional?: boolean;
}

/**
 * One cookie the native app reads by the start of its name (capture version 2).
 * The name can change for each user, so the app sends `{ name, value }`: the
 * full name and the value. The server checks that the name starts with
 * `namePrefix` and holds only `A-Z`, `a-z`, `0-9` and `_`.
 */
export interface ArtistPortalCapturedCookieByPrefix {
  /** The start of the cookie name, for example `COMPUTER_`. */
  namePrefix: string;
  /** The key of this value in `capturedSession` of `auth/complete`. */
  as: string;
}

/**
 * Every cookie of one host (capture version 4). The app sends each cookie of its store whose
 * domain is `allCookiesOf` or a subdomain of it (a `.host` domain counts too), as a list of
 * `{ name, value }`. The server keeps only names that are safe in a `Cookie` header.
 */
export interface ArtistPortalCapturedHostCookies {
  /** A host name, for example `artists.example.com`, with no scheme and no leading dot. */
  allCookiesOf: string;
  /** The key of the list in `capturedSession` of `auth/complete`. */
  as: string;
}

/** One cookie the native app reads from its web view after the sign-in. */
export type ArtistPortalCapturedCookie =
  | ArtistPortalCapturedCookieByName
  | ArtistPortalCapturedCookieByPrefix
  | ArtistPortalCapturedHostCookies;

/**
 * The captured-session part of a connection card.
 *
 *   - `available`: a native app can connect this service on this deploy.
 *   - `version`: the capture protocol the app must know. 1: each cookie of
 *     `capture` has `name` (Ditto Music). 2: a cookie can have `namePrefix`
 *     instead, and the app sends its full name with its value (DistroKid).
 *
 * An app that knows a lower version shows the waiting list.
 */
export interface ArtistPortalSessionCaptureCard {
  available: boolean;
  version: number;
}

/** The capture version of a service whose cookies all have an exact name. */
export const ARTIST_PORTAL_CAPTURE_VERSION_NAMED = 1;

/** The capture version of a service with a `namePrefix` cookie. */
export const ARTIST_PORTAL_CAPTURE_VERSION_PREFIX = 2;

/**
 * The capture version of a sign-in that is ready when its cookies are there (`readyWhen:
 * 'cookies'`) and that can have `optional` cookies (SoundCloud Insights, 2026-10-16).
 */
export const ARTIST_PORTAL_CAPTURE_VERSION_COOKIE_READY = 3;

/**
 * The capture version of a sign-in-only service (2026-10-18): a `capture` entry can name every
 * cookie of a host (`allCookiesOf`), and the card has `signInConsent`. The app shows the consent
 * line before the sign-in. An app that knows a lower version shows the waiting list.
 */
export const ARTIST_PORTAL_CAPTURE_VERSION_PENDING_SIGNIN = 4;

/**
 * The capture version of a sign-in-only service that needs the parts of 2026-10-19: the page
 * rule (`readyWhen: 'page'`) and the hard deny-list in the app (`isDeniedCaptureName`). The card also has `signInConsent`. An app that knows only version 4
 * (10.0.6) shows the waiting list for such a card, so it never sends a cookie that the deny-list
 * drops.
 */
export const ARTIST_PORTAL_CAPTURE_VERSION_PAGE_READY = 5;

/** The capture versions of a sign-in-only service. */
export const ARTIST_PORTAL_PENDING_SIGNIN_CAPTURE_VERSIONS: readonly number[] = [
  ARTIST_PORTAL_CAPTURE_VERSION_PENDING_SIGNIN,
  ARTIST_PORTAL_CAPTURE_VERSION_PAGE_READY,
];

/** A cookie that the app found by its prefix: the full name and the value. */
export interface ArtistPortalCapturedNamedValue {
  name: string;
  value: string;
}

/**
 * One captured value in `auth/complete`: a string for a `name` entry,
 * `{ name, value }` for a `namePrefix` entry, and a list of `{ name, value }` for an
 * `allCookiesOf` entry (capture version 4).
 */
export type ArtistPortalCapturedValue = string | ArtistPortalCapturedNamedValue | ArtistPortalCapturedNamedValue[];

/**
 * The page rule of capture version 5 (`readyWhen: 'page'`). The sign-in is done when the main
 * frame is on `host` (exact, no subdomain), its path fits `pathPrefixes` and `notPathPrefixes`,
 * and every cookie entry that is not `optional` has its cookie (an `allCookiesOf` entry: one
 * cookie at least, after the deny-list). The app checks after each page load and once a second
 * (a single-page app changes its address with no page load), with no time limit: the artist can
 * cancel.
 */
export interface ArtistPortalCaptureReadyPage {
  /** A host name in lower case, for example `dashboard.onerpm.com`. */
  host: string;
  /** The path must start with one of these. Absent means any path. */
  pathPrefixes?: string[];
  /** The path must not start with any of these (the sign-in pages). Absent means none. */
  notPathPrefixes?: string[];
}

export interface ArtistPortalSessionCapture {
  /** The url whose cookies the app reads, for example `https://dashboard.dittomusic.com/`. */
  cookieUrl: string;
  cookies: ArtistPortalCapturedCookie[];
  /**
   * Capture version 3. `cookies`: the app does not wait for `redirectUrlPrefix`. It reads the
   * cookie store after each page load and once a second, with no time limit, and stops when every
   * cookie that is not `optional` is there. Absent means: wait for the url prefix.
   * Capture version 5. `page`: the rule of `readyPage`.
   */
  readyWhen?: 'cookies' | 'page';
  /** Capture version 5, with `readyWhen: 'page'`. */
  readyPage?: ArtistPortalCaptureReadyPage;
}

/**
 * GET /accounts/:accountId/serviceConnections/:integrationType/artists
 *
 * The artists the stored sign-in can read. The app calls it after an
 * `oauthCallback` sign-in ends in `pickArtist`, because the artist list does
 * not travel through the redirect.
 */
export interface ArtistPortalArtistsResponse {
  artists: ArtistPortalArtistRef[];
}

/** POST …/auth/complete — one of the two bodies, by auth kind. */
export interface ArtistPortalAuthCompleteOAuthBody {
  sessionUuid: string;
  redirectUrl: string;
}
export interface ArtistPortalAuthCompleteCredentialsBody {
  email: string;
  password: string;
}
/** `capturedSession`: the values `auth/begin` named in `capture`, by their `as` key. */
export interface ArtistPortalAuthCompleteCapturedSessionBody {
  sessionUuid: string;
  capturedSession: Record<string, ArtistPortalCapturedValue>;
}
export type ArtistPortalAuthCompleteBody =
  | ArtistPortalAuthCompleteOAuthBody
  | ArtistPortalAuthCompleteCredentialsBody
  | ArtistPortalAuthCompleteCapturedSessionBody;

export interface ArtistPortalMetricTotal {
  current: number | null;
  previous: number | null;
  changePct: number | null;
}

export type ArtistPortalMetricTotals = Record<ArtistPortalMetric, ArtistPortalMetricTotal>;

export interface ArtistPortalSeriesPoint extends Partial<Record<ArtistPortalMetric, number>> {
  /** YYYY-MM-DD */
  date: string;
}

export interface ArtistPortalOverviewService {
  integrationType: string;
  status: ServiceConnectionStatus;
  syncStatus: ArtistPortalSyncStatus;
  syncedThrough: string | null;
  lastSyncAt: string | null;
  artist: ArtistPortalArtistRef | null;
  totals: ArtistPortalMetricTotals;
  series: ArtistPortalSeriesPoint[];
}

export interface ArtistPortalTopTrackByService {
  integrationType: string;
  externalId: string;
  streams: number | null;
  listeners: number | null;
  saves: number | null;
}

export interface ArtistPortalTopTrack {
  name: string;
  imageUrl: string | null;
  catalogItemUuid: string | null;
  byService: ArtistPortalTopTrackByService[];
  /** Sum of streams over the services that report the track. */
  streams: number;
}

/**
 * Numbers that only one source gives (Apple Music for Artists, 2026-10-21): the sums of the range.
 * Null when no row holds the number (no Apple Music connection, or no read yet).
 */
export interface ArtistPortalOverviewExtras {
  shazams: number | null;
  radioSpins: number | null;
}

/** GET /accounts/:accountId/artistPortals/overview?from&to */
export interface ArtistPortalOverviewResponse {
  range: { from: string; to: string };
  services: ArtistPortalOverviewService[];
  combined: { totals: ArtistPortalMetricTotals; series: ArtistPortalSeriesPoint[] };
  topTracks: ArtistPortalTopTrack[];
  /** Absent on a server from before 2026-10-21 and in the demo artist answer. */
  extras?: ArtistPortalOverviewExtras;
}

export interface ArtistPortalTrackRow {
  integrationType: string;
  externalId: string;
  name: string;
  imageUrl: string | null;
  releaseDate: string | null;
  totalStreams: number | null;
  streams28d: number | null;
  listeners28d: number | null;
  saves28d: number | null;
  catalogItemUuid: string | null;
  /** The ISRC, when the source gives one (a distributor does). Absent on an old server. */
  isrc?: string | null;
  /** The barcode (UPC or EAN) of the release, when the source gives one. */
  barcode?: string | null;
}

/** GET /accounts/:accountId/artistPortals/tracks?integrationType&sort&limit */
export interface ArtistPortalTracksResponse {
  tracks: ArtistPortalTrackRow[];
}

/** GET /accounts/:accountId/artistPortals/tracks/:integrationType/:externalId/daily?from&to */
export interface ArtistPortalTrackDailyResponse {
  track: ArtistPortalTrackRow;
  series: ArtistPortalSeriesPoint[];
}

export interface ArtistPortalPlaylistRow {
  integrationType: string;
  externalId: string;
  name: string;
  curatorType: ArtistPortalPlaylistCurator;
  curatorName: string | null;
  followers: number | null;
  streams28d: number | null;
  listeners28d: number | null;
  trackExternalIds: string[];
}

/** GET /accounts/:accountId/artistPortals/playlists */
export interface ArtistPortalPlaylistsResponse {
  /** ISO datetime of the newest snapshot per service, keyed by integrationType. */
  capturedAt: Record<string, string>;
  playlists: ArtistPortalPlaylistRow[];
}

/**
 * Private numbers by dimension (store or country). Ditto Music gives them
 * today. They come from Music24's own ClickHouse database, never from musicapi.
 */
export enum ArtistPortalDimension {
  store = 'store',
  country = 'country',
}

export interface ArtistPortalDimensionTotal {
  /** The store name, or the ISO 3166-1 alpha-2 country code. */
  value: string;
  /** Null when the source gave no number for the period. */
  streams: number | null;
  downloads: number | null;
  /**
   * The same numbers in `previousRange`, for the Change column of Streaming. Null when the source
   * gave no number there. Absent on an old server.
   */
  previousStreams?: number | null;
  previousDownloads?: number | null;
}

export interface ArtistPortalDimensionSeries {
  value: string;
  /** One point for each day that has a number. A day with no number is absent. */
  points: Array<{ date: string; streams?: number; downloads?: number }>;
}

/**
 * GET /accounts/:accountId/artistPortals/:integrationType/streams/byStore
 * GET /accounts/:accountId/artistPortals/:integrationType/streams/byCountry
 * Query: `days=7|28|90|180|365` or `from&to`, optional `trackExternalId`. The plan's
 * `historyDaysLimit` cuts the range.
 */
export interface ArtistPortalStreamsByDimensionResponse {
  /** False when this deploy has no private-metrics store. The lists are empty then. */
  available: boolean;
  range: { from: string; to: string };
  /**
   * The range of the same length directly before `range`, inside the plan window, or null when no
   * day of it is inside. Absent on an old server.
   */
  previousRange?: { from: string; to: string } | null;
  dimension: ArtistPortalDimension;
  /** The track the request named, or null for the artist total. */
  trackExternalId: string | null;
  /** Largest first, by streams. */
  totals: ArtistPortalDimensionTotal[];
  /** The daily series of the first values of `totals`. */
  series: ArtistPortalDimensionSeries[];
  /**
   * True when the plan of the workspace does not split the streams by store and country (Free).
   * `totals` and `series` are then empty. Absent when the plan shows the split.
   */
  splitHidden?: boolean;
}

export enum ArtistPortalEarningsGroup {
  store = 'store',
  track = 'track',
  period = 'period',
  /** By country: `label` is the ISO 3166-1 alpha-2 code (DistroKid; 2026-10-18). */
  country = 'country',
}

export interface ArtistPortalEarningsRow {
  /**
   * `store`: the store name. `track`: the track title. `period`: `YYYY-MM`. `country`: the ISO
   * 3166-1 alpha-2 code.
   */
  label: string;
  /** `track` only: the id of the track in `GET …/artistPortals/tracks`, when we know the track. */
  trackExternalId: string | null;
  /** `track` only. */
  isrc: string | null;
  /** Null when the source gives no gross amount for this row. */
  grossAmount: number | null;
  netAmount: number | null;
  /**
   * The units the source counts for the amount (DistroKid: the streams and downloads of the bank
   * report). A 0 is a real number. Null when the source does not give units (Ditto). Absent on an
   * old server.
   */
  quantity?: number | null;
}

/**
 * GET /accounts/:accountId/artistPortals/:integrationType/earnings?groupBy=store|track|period|country&from&to
 *
 * `from` and `to` are months (`YYYY-MM`). The answer has no balance, no payout
 * and no bank data: the server does not read or store them.
 */
export interface ArtistPortalEarningsResponse {
  available: boolean;
  range: { from: string; to: string };
  groupBy: ArtistPortalEarningsGroup;
  /**
   * True when the caller may not see money: a member of a workspace whose
   * owner did not turn "Members can see earnings" on, or an OAuth token.
   * `rows` is then null. The request is not refused, so the screen can show
   * the streams and say that the owner keeps the earnings private.
   */
  earningsHidden: boolean;
  /**
   * Why `earningsHidden` is true: `'plan'` when the plan of the workspace has no earnings (Free),
   * `'role'` when the caller may not see them. Absent when it is false and on an old server.
   */
  hiddenReason?: 'plan' | 'role';
  /**
   * ISO 4217, or null when the source does not say which currency it reports
   * in. Ditto Music: the currency the artist set in the Ditto settings. With
   * null, show the amounts with no currency sign and a note.
   *
   * It is the currency of `rows`. When the period holds amounts in more than
   * one currency (the artist changed the currency at the source), it is the
   * currency of the newest amounts, and the others are in `otherCurrencies`.
   */
  currency: string | null;
  /** The amounts in `currency`. Null when `earningsHidden` is true. */
  rows: ArtistPortalEarningsRow[] | null;
  /**
   * Only when the period holds amounts in more than one currency: the rows of
   * each other currency, the newest first. Amounts of two currencies are
   * never added. Absent in the usual case, so an older client reads the
   * answer as before.
   */
  otherCurrencies?: ArtistPortalEarningsOtherCurrency[];
}

/** The rows of one more currency of an earnings answer. */
export interface ArtistPortalEarningsOtherCurrency {
  /** ISO 4217, or null for the amounts that have no currency. */
  currency: string | null;
  rows: ArtistPortalEarningsRow[];
}
